<template>
    <div>

        <rkm-area
            title="Paciente"
            type="fieldset"
            no-margin-top
            v-if="paciente"
        >

            <rkm-row>
                <rkm-col class="flex-grow-1">
                    <rkm-row>
                        <rkm-col>
                            <h4>
                                {{ paciente.nome }}
                                <strong v-if="paciente.nomeSocial && paciente.preferenciaNomeId === enums.preferenciaNome.NOME_SOCIAL">
                                    ({{ paciente.nomeSocial }})
                                </strong>

                                <span
                                    v-if="paciente.responsavelFamiliar"
                                    class="ml-1"
                                    title="Responsável familiar"
                                    v-rkm-tooltip.hover
                                >
                                    <rkm-icon icon="crown" />
                                </span>

                                <span
                                    v-if="isTea"
                                    class="ml-1"
                                    title="Pessoa com Transtorno do Espectro Autista - TEA"
                                    v-rkm-tooltip.hover
                                >
                                    <rkm-icon icon="infinity" />
                                </span>

                                <span
                                    v-if="isPcd"
                                    class="ml-1"
                                    title="Pessoa com Deficiência - PCD"
                                    v-rkm-tooltip.hover
                                >
                                    <rkm-icon icon="wheelchair" />
                                </span>
                            </h4>
                        </rkm-col>
                    </rkm-row>

                    <rkm-row v-if="additionals && additionals.acompanhamentoSaude && additionals.acompanhamentoSaude.length > 0">
                        <rkm-col>
                            <div class="health-flags">
                                <span class="health-title">
                                    Acompanhamentos:
                                </span>

                                <span
                                    v-for="(acompanhamento, index) in additionals.acompanhamentoSaude"
                                    :key="index"
                                    :class="`health-badge acompanhamento-${acompanhamento.value}`"
                                >
                                    {{ acompanhamento.text }}
                                </span>
                            </div>
                        </rkm-col>
                    </rkm-row>

                    <rkm-row>
                        <rkm-col cols="4">
                            <p>
                                <strong>Prontuário:</strong>
                                {{ paciente.prontuario }}
                            </p>

                            <p>
                                <strong>CPF: </strong>
                                <template v-if="paciente.cpf">
                                    {{ paciente.cpf | formatCpf }}
                                </template>
                                <template v-else>
                                    <i>Não informado</i>
                                </template>
                            </p>

                            <p>
                                <strong>CNS: </strong>
                                <template v-if="paciente.cns">
                                    {{ paciente.cns | formatCns }}
                                </template>
                                <template v-else>
                                    <i>Não informado</i>
                                </template>
                            </p>

                            <p>
                                <strong>RG:</strong>
                                {{ paciente.rg || '-' }}
                            </p>

                            <p>
                                <strong>Telefone:</strong>
                                <template v-if="paciente.telefoneNumero">
                                    {{ paciente.telefoneNumero | formatPhoneOrCellPhone }}
                                </template>
                                <template v-else>
                                    -
                                </template>
                                <rkm-button
                                    v-if="paciente.telefoneId && verificarPermissao('update-cadastro-paciente')"
                                    @click="$refs.ModalTelefone.open(paciente)"
                                    class="ml-1"
                                    color="primary"
                                    title="Alterar telefone"
                                    v-rkm-tooltip.hover
                                    small
                                >
                                    <rkm-icon icon="pen" />
                                </rkm-button>
                            </p>
                        </rkm-col>

                        <rkm-col cols="4">
                            <p>
                                <strong>Data de nascimento: </strong>
                                <template v-if="paciente.dataNascimento">
                                    {{ paciente.dataNascimento | formatDate }}
                                </template>
                                <template v-else>
                                    <i>Não informado</i>
                                </template>
                            </p>

                            <p>
                                <strong>Idade: </strong>
                                <template v-if="paciente.idade">
                                    {{ paciente.idade.anos }} anos,
                                    {{ paciente.idade.meses }} meses e
                                    {{ paciente.idade.dias }} dias
                                </template>
                                <template v-else>
                                    <i>Não informado</i>
                                </template>
                            </p>

                            <p>
                                <strong>Sexo: </strong>
                                <template v-if="paciente.sexoDescricao">
                                    {{ paciente.sexoDescricao }}
                                </template>
                                <template v-else>
                                    <i>Não informado</i>
                                </template>
                            </p>

                            <p>
                                <strong>
                                    Nome da mãe:
                                </strong>
                                <template v-if="paciente.nomeMae">
                                    {{ paciente.nomeMae }}
                                </template>
                                <template v-else>
                                    <i>Desconhecido</i>
                                </template>
                            </p>

                            <p>
                                <strong>Cadastro individual: </strong>
                                <template v-if="paciente.hasCadastroIndividual">
                                    Vinculado
                                </template>
                                <template v-else>
                                    <span class="status-sem-cadastro-individual">Não vinculado</span>
                                </template>
                            </p>
                        </rkm-col>

                        <rkm-col cols="4">
                            <p>
                                <strong>Classificação:</strong>
                                {{ classificacaoDescricao }}
                            </p>

                            <p>
                                <strong>Unidade de referência:</strong>
                                <template v-if="paciente.unidadeReferencia">
                                    {{ paciente.unidadeReferencia }}
                                </template>
                                <template v-else>
                                    <i>Não informado no cadastro do paciente</i>
                                </template>
                            </p>

                            <p>
                                <strong>Área e Microárea:</strong>
                                {{ paciente.area || 'Não Informada' }} / {{ paciente.microarea || 'Não Informada' }}
                            </p>

                            <p>
                                <strong>INE:</strong>
                                {{ paciente.ine || 'Não Informado' }}
                            </p>
                        </rkm-col>
                    </rkm-row>

                    <rkm-row>
                        <rkm-col>
                            <p>
                                <strong>Endereço:</strong>
                                {{ paciente.endereco || 'Não informado' }}
                            </p>
                        </rkm-col>
                    </rkm-row>

                    <rkm-row
                        v-if="!paciente.hasCadastroIndividual"
                        class="mt-2"
                    >
                        <rkm-col>
                            <rkm-alert color="danger">
                                Paciente sem Cadastro Individual vinculado.
                            </rkm-alert>
                        </rkm-col>
                    </rkm-row>

                    <rkm-row
                        v-if="paciente.hasCadastroIndividual && paciente.diasCadastroIndividualDesatualizado > 0"
                        class="mt-2"
                    >
                        <rkm-col>
                            <rkm-alert color="warning">
                                Cadastro Individual desatualizado há <strong>{{ paciente.diasCadastroIndividualDesatualizado }}</strong>
                                {{ paciente.diasCadastroIndividualDesatualizado > 1 ? 'dias' : 'dia' }}.
                            </rkm-alert>
                        </rkm-col>
                    </rkm-row>
                </rkm-col>
            </rkm-row>
        </rkm-area>

        <ModalTelefone
            @saved="handleTelefoneSaved"
            ref="ModalTelefone"
        />

        <rkm-area
            title="Visitas do Paciente"
            type="fieldset"
            no-margin-top
        >

            <rkm-table
                :fields="fields"
                :items="items"
                sort-by="data"
            >

                <template v-slot:cell(data)="row">
                    {{ row.value | formatDate }}
                </template>

                <template v-slot:cell(dataExportacao)="{ item }">
                    {{ item.dataExportacao }} - {{ item.profissionalExportacao }}
                </template>

                <template v-slot:cell(actions)="{ item }">

                    <rkm-button
                        @click.stop="search(item)"
                        color="primary"
                        title="Visualizar"
                        small
                    >
                        <rkm-icon icon="search" />
                    </rkm-button>

                    <rkm-button
                        @click="handleDestroy(item)"
                        color="danger"
                        small
                        title="Excluir"
                    >
                        <rkm-icon icon="trash" />
                    </rkm-button>
                </template>
            </rkm-table>
        </rkm-area>

        <rkm-area
            title="Visitas no Domicílio"
            type="fieldset"
            no-margin-top
        >

            <rkm-table
                :fields="fieldsVisita"
                :items="visitas"
                sort-by="data"
            >

                <template v-slot:cell(data)="row">
                    {{ row.value | formatDate }}
                </template>

                <template v-slot:cell(dataExportacao)="{ item }">
                    {{ item.dataExportacao }} - {{ item.profissionalExportacao }}
                </template>

                <template v-slot:cell(actions)="{ item }">

                    <rkm-button
                        @click.stop="search(item)"
                        color="primary"
                        title="Visualizar"
                        small
                    >
                        <rkm-icon icon="search" />
                    </rkm-button>
                </template>
            </rkm-table>
        </rkm-area>
    </div>
</template>

<script>

import { mapActions } from 'vuex'

// Components.
import ModalTelefone from '@/components/ambulatorio/components/paciente/telefone/Modal'

// Mixins.
import { repository, error, printExameLaboratorial, permissao, stringCase, unmask } from '@/mixins'

// Filters.
import format from '@/filters/formats'

//Enuns.
import preferenciaNome from '@/enums/cadastro/cidadao/preferenciaNome'
import classificacao from '@/enums/cadastro/paciente/classificacao'
import subclassificacao from '@/enums/cadastro/paciente/subclassificacao'
import deficienciaBase from '@/enums/cadastro/cidadao/deficienciaBase'
import deficiencia from '@/enums/cadastro/cidadao/deficiencia'

export default {
    components: {
        ModalTelefone,
    },
    computed: {
        classificacaoDescricao() {

            if (this.paciente.subclassificacaoId == this.enums.subclassificacao.TEMPORARIO) {
                return 'Temporário'
            }

            if (this.paciente.classificacaoId == this.enums.classificacao.OUTRO_MUNICIPIO) {
                return 'Outro Município'
            }

            return 'Munícipe'
        },
        isTea() {
            const ids = this.paciente.deficienciasBaseIds || []

            return ids.includes(this.enums.deficienciaBase.TEA)
                || this.paciente.deficienciaId === this.enums.deficiencia.AUTISMO
        },
        isPcd() {
            const ids = this.paciente.deficienciasBaseIds || []

            return ids.some(id => id !== this.enums.deficienciaBase.TEA)
                || (!!this.paciente.deficienciaId && this.paciente.deficienciaId !== this.enums.deficiencia.AUTISMO)
        },
    },
    created() {

        this.repositories = {
            visitaDomiciliar: this.getConectaSusRepository('atencaoBasica.visitaDomiciliar')
        }

        this.load()
    },
    data: () => ({
        additionals: null,
        fields: [
            {
                key: 'data',
                label: 'Data',
                sortable: true
            },
            {
                key: 'turno',
                label: 'Turno',
                sortable: true
            },
            {
                key: 'imovelTipo',
                label: 'Tipo de imóvel',
                sortable: true
            },
            {
                key: 'profissional',
                label: 'Profissional',
                sortable: true
            },
            {
                key: 'dataExportacao',
                label: 'Exportação',
            },
            {
                key: 'actions',
                label: 'Ações',
                class: 'col-min'
            },
        ],
        fieldsVisita: [
            {
                key: 'data',
                label: 'Data',
                sortable: true
            },
            {
                key: 'paciente',
                label: 'Paciente',
                sortable: true
            },
            {
                key: 'turno',
                label: 'Turno',
                sortable: true
            },
            {
                key: 'imovelTipo',
                label: 'Tipo de imóvel',
                sortable: true
            },
            {
                key: 'profissional',
                label: 'Profissional',
                sortable: true
            },
            {
                key: 'dataExportacao',
                label: 'Exportação',
            },
            {
                key: 'actions',
                label: 'Ações',
                class: 'col-min'
            },
        ],
        repositories: {
            paciente: null,
        },
        items: [],
        visitas: [],
        paciente: null,
        enums: {
            preferenciaNome,
            classificacao,
            subclassificacao,
            deficienciaBase,
            deficiencia,
        },
    }),
    filters: {
        ...format,
    },
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {

            if (this.id) {
                this.setLoading()

                this.repositories.visitaDomiciliar.getAtendimentos({ id : this.id })
                    .then(response => {

                        this.additionals = response.data.additionals
                        this.items = Object.assign(response.data.atendimentos)
                        this.visitas = Object.assign(response.data.visitasDomiciliar)
                        this.paciente = Object.assign(response.data.cidadao)

                        this.loaded = true

                        this.$emit('loaded', {additionals: this.additionals, cidadao: this.paciente})
                    })
                    .catch(this.handleError)
                    .finally(() => this.unsetLoading())
            }
        },
        handleTelefoneSaved() {

            const request = {
                id: this.paciente.telefoneId,
                numero: this.unMaskPhone(this.paciente.telefoneNumero),
                autorizaSms: this.paciente.telefoneAutorizaSms,
            }

            this.repositories.visitaDomiciliar.updateTelefone(this.camelObjectKeysToSnakeRecursive(request))
                .then(() => this.showSaveNotification())
                .catch(error => {
                    this.handleError(error)
                    this.load()
                })
        },
        search(item) {

            this.$router.push({ name: 'atencao-basica-visita-domiciliar-edit', params: { id: item.id, pacienteId: this.id, visitasRealizadas: this.items }})
        },
        handleCadastrar() {

            let visitasMesmoDia = this.items.concat(this.visitas).filter(visita => visita.data == this.additionals.dataAtual)

            if (visitasMesmoDia.length > 0) {

                let nomes = visitasMesmoDia.map(v => v.paciente)
                let nomesUnicos = [...new Set(nomes)]
                let prefixo = nomes.length > 1 ? 'Os pacientes' : 'O paciente'
                let listaNomes = nomesUnicos.join(', ').replace(/, ([^,]*)$/, ' e $1')

                let msg = `${prefixo} <strong>${listaNomes}</strong>, do mesmo domicílio, já ${
                    nomes.length > 1 ? 'possuem' : 'possui'
                } visitas registradas para <strong>${this.additionals.dataAtual.split('-').reverse().join('/')}</strong>.
                Deseja continuar com o cadastro?`

                this.showWarningConfirmation(
                    'Atenção',
                    `${msg}`,
                    async () => this.$router.push({ name: 'atencao-basica-visita-domiciliar-create', params: { pacienteId: this.id, visitasRealizadas: this.items }}),

                )
            } else {

                this.$router.push({ name: 'atencao-basica-visita-domiciliar-create', params: { pacienteId: this.id, visitasRealizadas: this.items }})

            }
        },
        handleDestroy({ id }) {

            const destroy = () => this.repositories.visitaDomiciliar.destroy(id)
                .then(() => this.items.splice(this.items.findIndex(i => i.id === id), 1))

            this.showDeleteConfirmation('Deseja realmente excluir o atendimento?', destroy, this.handleError)
        },
    },
    mixins: [ repository, error, printExameLaboratorial, permissao, stringCase, unmask ],
    props: {
        id: { type: Number, default: null }
    },
}
</script>

<style scoped>
p {
    margin: 0px;
}

.health-flags {
    margin-top: 9px;
    padding-top: 2px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    border-top: 1px solid #e5e7eb;
}

.health-title {
    font-size: 12px;
    color: #6b7280;
    font-weight: 600;
}

.health-badge {
    padding: 5px 10px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
}

.acompanhamento-idoso {
    color: #155724;
}
.acompanhamento-gestante {
    color: #ffb6c1;
}
.acompanhamento-hipertenso {
    color: #856404;
}
.acompanhamento-diabetico {
    color: #856404;
}
.acompanhamento-saude-mulher {
    color: #b34db2;
}
.acompanhamento-crescimento-desenvolvimento-infantil {
    color: #004085;
}

.status-sem-cadastro-individual {
    color: var(--danger);
    font-weight: 600;
}
</style>